import React, { useState, useContext } from "react";
import { Box, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import { getToken } from "../../../../api/Account";
import { tienePermiso } from "../../../../helpers/permisos";
import { DataContext } from "../../../../context/DataContext";
import { useAviso } from "../../../../components/base";
import { formatearFecha } from "../../../../helpers/formato";
import {
  deleteVacacionesByIdAsync,
  getVacacionesAsync,
} from "../../../../api/Employees";
import AddVacaciones from "../vacaciones/AddVacaciones";
import UpdateVacaciones from "../vacaciones/UpdateVacaciones";
import PrintComprobante from "../vacaciones/PrintComprobante";
import MediumModal from "../../../../components/modals/MediumModal";
import HistorialTab from "./HistorialTab";
import {
  DatoResumen,
  celdaFecha,
  formatearAntiguedadServidor,
  periodo,
  useConfirmacion,
  useEscritura,
  useListaRemota,
} from "../comunes";

// Saldo de vacaciones sobre la tabla: lo que calcula el servidor, sin tocarlo.
const ResumenVacaciones = ({ datos }) => (
  <Box
    component="dl"
    aria-label="Saldo de vacaciones"
    sx={{
      m: 0,
      mb: 2,
      p: 2,
      borderRadius: 2.5,
      display: "grid",
      gap: 2,
      gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
      bgcolor: (theme) => theme.palette.soft.primary,
    }}
  >
    <DatoResumen etiqueta="Antigüedad">
      <Typography variant="monto" component="span">
        {formatearAntiguedadServidor(datos.antiguedad)}
      </Typography>
    </DatoResumen>
    <DatoResumen etiqueta="Acumuladas">
      <Typography variant="monto" component="span">
        {datos.vacAcum}
      </Typography>
    </DatoResumen>
    <DatoResumen etiqueta="Tomadas">
      <Typography variant="monto" component="span">
        {datos.vacTomadas}
      </Typography>
    </DatoResumen>
    <DatoResumen etiqueta="Saldo">
      <Typography variant="monto" component="span">
        {datos.saldoVac}
      </Typography>
    </DatoResumen>
  </Box>
);

const Vacaciones = ({ id }) => {
  const [selectedVac, setSelectedBac] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir, dialogo } = useConfirmacion();

  const {
    datos: dataEmployee,
    cargando,
    error,
    reload: localReload,
    setReload: setLocalReload,
  } = useListaRemota(() => getVacacionesAsync(token, id));
  const histoyVac = dataEmployee.historialVac ?? [];

  const deleteVacacion = async (vacacionId) => {
    const ok = await escribir(
      () => deleteVacacionesByIdAsync(token, vacacionId),
      "No se pudo eliminar el registro de vacaciones"
    );
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito("Vacaciones eliminadas");
  };

  const pedirEliminar = (row) =>
    pedir({
      titulo: "Eliminar vacaciones",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se eliminarán las vacaciones #${row.id}${
              row.motivo ? ` «${row.motivo}»` : ""
            } (${periodo(row.desde, row.hasta)}, ${row.diasEfectivos} días efectivos).`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Esta acción no se puede deshacer.
          </Typography>
        </>
      ),
      textoConfirmar: "Eliminar vacaciones",
      peligro: true,
      onConfirmar: () => deleteVacacion(row.id),
    });

  // Agregar es el Add... de EmployeesController/NominasController (NOMINA CREAR).
  const puedeAgregar = tienePermiso(access, "NOMINA CREAR");
  const puedeEditar = tienePermiso(access, "NOMINA UPDATE");
  // La pantalla pedía NOMINA DELETE y DeleteVacaciones exige NOMINA UPDATE en el servidor: se piden los dos.
  const puedeEliminar =
    tienePermiso(access, "NOMINA DELETE") && tienePermiso(access, "NOMINA UPDATE");
  const activo = Boolean(dataEmployee.isActive);

  const columnas = [
    {
      id: "id",
      etiqueta: "#",
      desde: "md",
      sx: { fontVariantNumeric: "tabular-nums" },
      render: (row) => row.id,
    },
    {
      id: "solicitud",
      etiqueta: "Solicitud",
      desde: "md",
      sx: celdaFecha,
      render: (row) => formatearFecha(row.fechaSolicitud),
    },
    {
      id: "descripcion",
      etiqueta: "Descripción",
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {row.motivo}
        </Typography>
      ),
    },
    {
      id: "periodo",
      etiqueta: "Inicio – Retorno",
      desde: "sm",
      sx: celdaFecha,
      render: (row) => periodo(row.desde, row.hasta),
    },
    {
      id: "dias",
      etiqueta: "Días efectivos",
      alinear: "right",
      sx: { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
      render: (row) => row.diasEfectivos,
    },
    {
      id: "pagadas",
      etiqueta: "Pagadas",
      desde: "md",
      render: (row) => (row.pagadas ? "Sí" : "No"),
    },
  ];

  return (
    <div>
      <HistorialTab
        titulo="Vacaciones"
        descripcion="Saldo de vacaciones del empleado y las que ya tomó."
        etiquetaTabla="Historial de vacaciones"
        agregar={
          activo && puedeAgregar
            ? { etiqueta: "Agregar vacaciones", onClick: () => setShowModal(true) }
            : undefined
        }
        cargando={cargando}
        error={error}
        onReintentar={() => setLocalReload(!localReload)}
        etiquetaCarga="Cargando vacaciones…"
        resumen={cargando || error ? null : <ResumenVacaciones datos={dataEmployee} />}
        filas={histoyVac}
        columnas={columnas}
        detalleMovil={(row) => (
          <>
            {periodo(row.desde, row.hasta)}
            {` · ${row.pagadas ? "Pagadas" : "No pagadas"}`}
          </>
        )}
        etiquetaFila={(row) => `vacaciones #${row.id}`}
        vacio={{
          titulo: "Sin vacaciones",
          descripcion: "Aquí aparecen las vacaciones que toma el empleado.",
        }}
        acciones={(row) => [
          {
            id: "imprimir",
            etiqueta: "Imprimir comprobante",
            icono: <PrintOutlinedIcon />,
            visible: activo,
            onClick: () => {
              setSelectedBac(row);
              setShowPrintModal(true);
            },
          },
          {
            id: "editar",
            etiqueta: "Editar",
            icono: <EditOutlinedIcon />,
            visible: activo && puedeEditar,
            onClick: () => {
              setSelectedBac(row);
              setShowEditModal(true);
            },
          },
          {
            id: "eliminar",
            etiqueta: "Eliminar",
            icono: <DeleteOutlineIcon />,
            peligro: true,
            visible: activo && puedeEliminar,
            onClick: () => pedirEliminar(row),
          },
        ]}
      />

      {showModal ? (
        <AddVacaciones
          id={id}
          setShowModal={setShowModal}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      {showEditModal ? (
        <UpdateVacaciones
          selected={selectedVac}
          setShowModal={setShowEditModal}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      <MediumModal
        titulo={`Comprobante de Solicitud de Vacaciones`}
        isVisible={showPrintModal}
        setVisible={setShowPrintModal}
      >
        <PrintComprobante selected={selectedVac} setShowModal={setShowPrintModal} />
      </MediumModal>

      {dialogo}
    </div>
  );
};

export default Vacaciones;
